// EVM adapter — Ethereum today, any EVM chain by passing a different network.
// - HD derivation (m/44'/60'/0'/0/index) — pure, runs client-side at wallet creation.
// - ERC-20 USDT/USDC balance reads via an Alchemy (or any) RPC URL.
// Gasless transfers (Alchemy Gas Manager / ERC-4337) land in phase 2.

import { HDKey } from '@scure/bip32';
import { createPublicClient, http, erc20Abi, getAddress, bytesToHex, formatUnits, parseUnits } from 'viem';
import { privateKeyToAddress } from 'viem/accounts';
import type { Chain as ViemChain } from 'viem';
import { bsc, hyperEvm, mainnet } from 'viem/chains';
import {
  TOKENS,
  nativeForChain,
  requireTokenInfo,
  type Chain,
  type ContractChain,
  type TokenSymbol,
} from '@incpt/shared';
import type { DerivedAddresses, Address, IncomingTransfer } from '../types';

const PATH = (index: number) => `m/44'/60'/0'/0/${index}`;

/**
 * Which EVM chain a read is against.
 *
 * Every function here used to hardcode viem's `mainnet` and `TOKENS.eth`, so
 * the adapter could only ever speak to Ethereum even though BNB Smart Chain
 * and HyperEVM need exactly the same code with a different chain id and token
 * table. `chain` is our own key (it indexes `TOKENS` and the DB enum), `viem`
 * is the chain object the client needs for fee estimation and multicall.
 *
 * Callers that do not pass one get Ethereum, which is what they had before.
 */
export interface EvmNetwork {
  chain: ContractChain;
  viem: ViemChain;
}

export const ETHEREUM: EvmNetwork = { chain: 'eth', viem: mainnet };
export const BSC: EvmNetwork = { chain: 'bsc', viem: bsc };
export const HYPEREVM: EvmNetwork = { chain: 'hyperevm', viem: hyperEvm };

/** The EVM network for a chain, throwing when it is not an EVM one. Use at a
 *  call site that has already established the chain is EVM, so the impossible
 *  case raises instead of being waved through with a non-null assertion. */
export function requireEvmNetwork(chain: Chain): EvmNetwork {
  const net = evmNetworkFor(chain);
  if (!net) throw new Error(`${chain} is not an EVM chain`);
  return net;
}

/** The EVM network for one of our chain keys, or null when it is not an EVM chain. */
export function evmNetworkFor(chain: Chain): EvmNetwork | null {
  switch (chain) {
    case 'eth':
      return ETHEREUM;
    case 'bsc':
      return BSC;
    case 'hyperevm':
      return HYPEREVM;
    case 'trx':
    case 'sol':
    case 'ton':
    case 'btc':
    case 'zec':
    case 'xmr':
    case 'doge':
    case 'xrp':
      return null;
    default: {
      const never: never = chain;
      throw new Error(`evmNetworkFor: unhandled chain ${String(never)}`);
    }
  }
}

/** Derive the EVM address + public key at the given account index. */
export function deriveEvmAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.privateKey || !child.publicKey) throw new Error('EVM derivation failed');
  return {
    eoa: privateKeyToAddress(bytesToHex(child.privateKey)),
    publicKey: bytesToHex(child.publicKey),
  };
}

/** Raw on-chain balance (token base units, 6 decimals for USDT/USDC). */
export async function getEvmBalance(
  rpcUrl: string,
  address: Address,
  token: TokenSymbol,
  net: EvmNetwork = ETHEREUM,
): Promise<bigint> {
  const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
  return client.readContract({
    address: getAddress(requireTokenInfo(net.chain, token).address),
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [getAddress(address)],
  });
}

/** Native ETH balance in wei. */
export async function getEvmNativeBalance(
  rpcUrl: string,
  address: Address,
  net: EvmNetwork = ETHEREUM,
): Promise<bigint> {
  const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
  return client.getBalance({ address: getAddress(address) });
}

/**
 * Current gas price in wei for fee estimation. Returns viem's `maxFeePerGas`
 * (≈ baseFee × 2 + priority tip), NOT the raw current price — a sponsored op
 * mines a block or two later, so biasing toward the higher fee-cap protects the
 * project from under-collecting when the fee is fixed pre-mining. Falls back to
 * `getGasPrice` on chains/RPCs that don't support EIP-1559 fee estimation.
 */
export async function getEvmGasPrice(
  rpcUrl: string,
  net: EvmNetwork = ETHEREUM,
): Promise<bigint> {
  const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
  try {
    const { maxFeePerGas } = await client.estimateFeesPerGas();
    if (maxFeePerGas && maxFeePerGas > 0n) return maxFeePerGas;
  } catch {
    /* legacy chain / RPC without 1559 estimation → fall through */
  }
  return client.getGasPrice();
}

/**
 * On-chain execution result of a broadcast Ethereum transaction, by hash.
 * Mirrors getTronTxResult so callers can verify a payment actually settled:
 *   'success' — mined, receipt status 1 (transfer executed),
 *   'failed'  — mined but reverted (receipt status 0),
 *   'unknown' — not yet mined, or the RPC couldn't be read.
 */
export async function getEvmTxResult(
  rpcUrl: string,
  txHash: string,
  net: EvmNetwork = ETHEREUM,
): Promise<'success' | 'failed' | 'unknown'> {
  try {
    const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
    const receipt = await client
      .getTransactionReceipt({ hash: txHash as `0x${string}` })
      .catch(() => null);
    if (!receipt) return 'unknown';
    return receipt.status === 'success' ? 'success' : 'failed';
  } catch {
    return 'unknown';
  }
}

/** Resolve a mined ETH txHash from an ERC-4337 UserOperation hash, or null
 *  when the bundler has not included it yet / the RPC is unavailable. */
export async function getUserOperationTxHash(
  rpcUrl: string,
  userOpHash: string,
  net: EvmNetwork = ETHEREUM,
): Promise<string | null> {
  try {
    const res = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'eth_getUserOperationReceipt',
        params: [userOpHash],
      }),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as {
      result?: { receipt?: { transactionHash?: string } } | null;
    };
    const hash = json.result?.receipt?.transactionHash;
    return typeof hash === 'string' && /^0x[0-9a-fA-F]{64}$/.test(hash) ? hash.toLowerCase() : null;
  } catch {
    return null;
  }
}

const TRANSFER_EVENT_TOPIC =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

const topicAddress = (topic: string | undefined): string | null => {
  if (!topic || !/^0x[0-9a-f]{64}$/i.test(topic)) return null;
  return `0x${topic.slice(-40)}`.toLowerCase();
};

/** Verify that a successful EVM transaction emitted the exact ERC-20 Transfer
 * expected by the server. Receipt success alone is not payment proof. */
export async function verifyEvmTokenTransfer(
  rpcUrl: string,
  txHash: string,
  expected: {
    token: TokenSymbol;
    from: string;
    to: string;
    amount: string;
  },
  net: EvmNetwork = ETHEREUM,
): Promise<boolean | null> {
  try {
    const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
    const receipt = await client
      .getTransactionReceipt({ hash: txHash as `0x${string}` })
      .catch(() => null);
    if (!receipt) return null;
    if (receipt.status !== 'success') return false;

    const expectedToken = requireTokenInfo(net.chain, expected.token);
    const token = expectedToken.address.toLowerCase();
    const from = getAddress(expected.from).toLowerCase();
    const to = getAddress(expected.to).toLowerCase();
    const amount = parseUnits(expected.amount, expectedToken.decimals);
    return receipt.logs.some((log) => {
      if (
        log.address.toLowerCase() !== token ||
        log.topics[0]?.toLowerCase() !== TRANSFER_EVENT_TOPIC ||
        topicAddress(log.topics[1]) !== from ||
        topicAddress(log.topics[2]) !== to
      ) {
        return false;
      }
      try {
        return BigInt(log.data) === amount;
      } catch {
        return false;
      }
    });
  } catch {
    return null;
  }
}

/** Verify the sender, recipient and value of a mined native ETH transfer. */
export async function verifyEvmNativeTransfer(
  rpcUrl: string,
  txHash: string,
  expected: { from: string; to: string; amountWei: bigint },
  net: EvmNetwork = ETHEREUM,
): Promise<boolean | null> {
  try {
    const client = createPublicClient({ chain: net.viem, transport: http(rpcUrl) });
    const [tx, receipt] = await Promise.all([
      client.getTransaction({ hash: txHash as `0x${string}` }).catch(() => null),
      client.getTransactionReceipt({ hash: txHash as `0x${string}` }).catch(() => null),
    ]);
    if (!tx || !receipt) return null;
    if (receipt.status !== 'success' || !tx.to) return false;
    return (
      tx.from.toLowerCase() === getAddress(expected.from).toLowerCase() &&
      tx.to.toLowerCase() === getAddress(expected.to).toLowerCase() &&
      tx.value === expected.amountWei
    );
  } catch {
    return null;
  }
}

// Lower-cased contract address → {symbol, decimals} for mapping Alchemy results.
// Built per network and memoized: the same USDT symbol is a different contract
// on each EVM chain, so a single Ethereum-only map would silently drop every
// transfer on any other one.
type TokenByAddr = Map<string, { sym: TokenSymbol; decimals: number }>;
const tokenByAddrCache = new Map<ContractChain, TokenByAddr>();

function tokenByAddr(net: EvmNetwork): TokenByAddr {
  const hit = tokenByAddrCache.get(net.chain);
  if (hit) return hit;
  const built: TokenByAddr = new Map(
    (Object.entries(TOKENS[net.chain]) as [TokenSymbol, { address: string; decimals: number }][])
      .map(([sym, info]) => [info.address.toLowerCase(), { sym, decimals: info.decimals }]),
  );
  tokenByAddrCache.set(net.chain, built);
  return built;
}

interface AlchemyAssetTransfer {
  hash: string;
  uniqueId?: string;
  category?: string;
  from: string;
  to: string;
  rawContract: { value: string; address: string | null; decimal: string };
  metadata?: { blockTimestamp?: string };
}

async function alchemyAssetTransfers(
  rpcUrl: string,
  params: Record<string, unknown>,
): Promise<AlchemyAssetTransfer[]> {
  const body = {
    id: 1,
    jsonrpc: '2.0',
    method: 'alchemy_getAssetTransfers',
    params: [params],
  };
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`alchemy_getAssetTransfers HTTP ${res.status}`);
  const json = (await res.json()) as {
    result?: { transfers?: AlchemyAssetTransfer[] };
    error?: { message: string };
  };
  if (json.error) throw new Error(`alchemy_getAssetTransfers: ${json.error.message}`);
  return json.result?.transfers ?? [];
}

function mapEthTransfers(
  transfers: AlchemyAssetTransfer[],
  net: EvmNetwork,
): IncomingTransfer[] {
  const native = nativeForChain(net.chain);
  const out: IncomingTransfer[] = [];
  for (const tr of transfers) {
    const common = {
      chain: net.chain,
      txHash: tr.hash,
      from: tr.from,
      to: tr.to,
      timestamp: tr.metadata?.blockTimestamp ? Date.parse(tr.metadata.blockTimestamp) : Date.now(),
    };
    if (tr.category === 'external' || tr.category === 'internal') {
      // Native ETH deposit — rawContract.value is hex wei, no contract address.
      // A chain we don't list a coin for can still carry its tokens; skip the
      // coin leg rather than inventing a symbol for it.
      if (!native) continue;
      out.push({
        ...common,
        token: native,
        uniqueId: tr.uniqueId ?? `${tr.hash}:${tr.category}`,
        amount: formatUnits(BigInt(tr.rawContract.value), 18),
      });
      continue;
    }
    const meta = tokenByAddr(net).get(tr.rawContract?.address?.toLowerCase() ?? '');
    if (!meta) continue;
    out.push({
      ...common,
      token: meta.sym,
      uniqueId: tr.uniqueId ?? `${tr.hash}:${tr.rawContract.address}`,
      amount: formatUnits(BigInt(tr.rawContract.value), meta.decimals),
    });
  }
  return out;
}

/**
 * Incoming transfers to `address` via Alchemy's `alchemy_getAssetTransfers`
 * enhanced API — ERC-20 USDT/USDC AND native ETH. Native ETH needs BOTH
 * 'external' (EOA-sent) and 'internal' (contract-sent — most exchange hot
 * wallets withdraw through contracts) or deposits from e.g. Coinbase would be
 * invisible. Tokens are queried by contract so spam ERC-20s cannot crowd
 * USDT/USDC out of a single mixed page. Newest first. Throws only when every
 * query fails.
 */
export async function getIncomingEthTransfers(
  rpcUrl: string,
  address: Address,
  maxCount = 100,
  net: EvmNetwork = ETHEREUM,
): Promise<IncomingTransfer[]> {
  const base = {
    toAddress: address,
    order: 'desc' as const,
    maxCount: `0x${maxCount.toString(16)}`,
    excludeZeroValue: true,
    withMetadata: true,
  };
  const contracts = [...tokenByAddr(net).keys()];

  const nativeP = alchemyAssetTransfers(rpcUrl, {
    ...base,
    category: ['external', 'internal'],
  }).then(
    (rows) => ({ ok: true as const, rows }),
    (e: unknown) => ({ ok: false as const, error: e }),
  );
  const tokenP = alchemyAssetTransfers(rpcUrl, {
    ...base,
    category: ['erc20'],
    contractAddresses: contracts,
  }).then(
    (rows) => ({ ok: true as const, rows }),
    async (filteredError: unknown) => {
      // Some addresses reject contractAddresses (-32000). Fall back to an
      // unfiltered ERC-20 page and keep only our tokens in mapEthTransfers.
      try {
        const rows = await alchemyAssetTransfers(rpcUrl, { ...base, category: ['erc20'] });
        return { ok: true as const, rows };
      } catch {
        return { ok: false as const, error: filteredError };
      }
    },
  );

  const [native, tokens] = await Promise.all([nativeP, tokenP]);
  if (!native.ok && !tokens.ok) {
    throw native.error instanceof Error ? native.error : new Error(String(native.error));
  }
  return mapEthTransfers(
    [...(native.ok ? native.rows : []), ...(tokens.ok ? tokens.rows : [])],
    net,
  );
}
